import { apiRequest } from "./queryClient";

export interface User {
  id: string;
  email: string;
  role: string;
  firstName: string;
  lastName: string;
  dateOfJoining?: string;
  department?: string;
  designation?: string;
  workLocation?: string;
}

export interface UserWithBuddy extends User {
  buddyUserId?: number | null;
  buddyFirstName?: string | null;
  buddyLastName?: string | null;
  buddyEmail?: string | null;
  buddyDepartmentId?: number | null;
  buddyDesignationId?: number | null;
  buddyOfficeLocationId?: number | null;
  buddyDateOfJoining?: string | null;
  buddyProfilePictureUrl?: string | null;
  buddyDepartmentName?: string | null;
  buddyDesignationTitle?: string | null;
  buddyOfficeLocationName?: string | null;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData extends LoginCredentials {
  firstName: string;
  lastName: string;
  role?: string;
}

export class AuthService {
  /**
   * Authenticate user with email and password
   */
  static async login(credentials: LoginCredentials): Promise<{ user: User }> {
    const response = await apiRequest("POST", "/auth/login", credentials);
    return response.json();
  }

  /**
   * Logout the current user
   */
  static async logout(): Promise<void> {
    await apiRequest("POST", "/auth/logout");
  }

  /**
   * Get current authenticated user
   */
  static async getCurrentUser(): Promise<{ user: User } | null> {
    try {
      const response = await apiRequest("GET", "/auth/me");
      return response.json();
    } catch (error) {
      // If unauthorized, return null instead of throwing
      if (error instanceof Error && error.message.includes('401')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Get current authenticated user with buddy information
   */
  static async getCurrentUserWithBuddy(): Promise<{ user: UserWithBuddy } | null> {
    try {
      const response = await apiRequest("GET", "/auth/me-with-buddy");
      return response.json();
    } catch (error) {
      // If unauthorized, return null instead of throwing
      if (error instanceof Error && error.message.includes('401')) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Check if user has specific role
   */
  static hasRole(user: User | null, role: string): boolean {
    return user?.role === role;
  }

  /**
   * Check if user has any of the specified roles
   */
  static hasAnyRole(user: User | null, roles: string[]): boolean {
    return user ? roles.includes(user.role) : false;
  }

  /**
   * Get user display name
   */
  static getDisplayName(user: User): string {
    return `${user.firstName} ${user.lastName}`;
  }

  /**
   * Get user initials for avatar
   */
  static getInitials(user: User): string {
    return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
  }

  /**
   * Check if user is admin
   */
  static isAdmin(user: User | null): boolean {
    return this.hasRole(user, 'admin');
  }

  /**
   * Check if user is employee
   */
  static isEmployee(user: User | null): boolean {
    return this.hasRole(user, 'employee');
  }

  /**
   * Check if user is candidate
   */
  static isCandidate(user: User | null): boolean {
    return this.hasRole(user, 'candidate');
  }

  /**
   * Validate email format
   */
  static isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate password strength
   */
  static validatePassword(password: string): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    if (password.length < 8) {
      errors.push("Password must be at least 8 characters long");
    }

    if (!/[A-Z]/.test(password)) {
      errors.push("Password must contain at least one uppercase letter");
    }

    if (!/[a-z]/.test(password)) {
      errors.push("Password must contain at least one lowercase letter");
    }

    if (!/\d/.test(password)) {
      errors.push("Password must contain at least one number");
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

/**
 * Hook-like function to get user role display text
 */
export function getRoleDisplayText(role: string): string {
  switch (role) {
    case 'admin':
      return 'Administrator';
    case 'employee':
      return 'Employee';
    case 'candidate':
      return 'Candidate';
    default:
      return 'User';
  }
}

/**
 * Get role-specific dashboard path
 */
export function getRoleDashboardPath(role: string): string {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'candidate':
      return '/candidate';
    default:
      return '/';
  }
}

/**
 * Check if user can access admin features
 */
export function canAccessAdminFeatures(user: User | null): boolean {
  return AuthService.isAdmin(user);
}

/**
 * Check if user can manage training modules
 */
export function canManageTrainingModules(user: User | null): boolean {
  return AuthService.isAdmin(user);
}

/**
 * Check if user can view all feedback
 */
export function canViewAllFeedback(user: User | null): boolean {
  return AuthService.isAdmin(user);
}

/**
 * Check if user can create quizzes
 */
export function canCreateQuizzes(user: User | null): boolean {
  return AuthService.isAdmin(user);
}

/**
 * Get available navigation items based on user role
 */
export function getNavigationItems(role: string) {
  const commonItems = [
    { href: "/", icon: "Home", label: "Dashboard" },
  ];

  switch (role) {
    case 'admin':
      return [
        ...commonItems,
        { href: "/training", icon: "GraduationCap", label: "Training Modules" },
        { href: "/quiz/manage", icon: "ClipboardCheck", label: "Manage Quizzes" },
        { href: "/analytics", icon: "BarChart3", label: "Analytics" },
        { href: "/users", icon: "Users", label: "Manage Users" },
        { href: "/feedback", icon: "MessageSquare", label: "View Feedback" },
        { href: "/settings", icon: "Settings", label: "Settings" },
      ];
    case 'candidate':
      return [
        { href: "/", icon: "Home", label: "Dashboard" },
      ];
    default: // employee
      return [
        ...commonItems,
        { href: "/training", icon: "GraduationCap", label: "Training Modules" },
        { href: "/quiz", icon: "ClipboardCheck", label: "Knowledge Checks" },
        { href: "/progress", icon: "TrendingUp", label: "Progress" },
        { href: "/feedback", icon: "MessageSquare", label: "Feedback" },
      ];
  }
}
